import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  useCategories,
  useCreateRole,
  usePermissions,
  useUpdateRole,
  type CategoryAccess,
  type RoleRow,
} from "@/lib/rbac";

type Access = CategoryAccess | "none";

function humanize(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Create/edit a role. The category matrix is the primary control — one
 * None/Read/Write choice per sidebar section, covering every feature in that
 * section now and in the future (live expansion, see backend
 * service.expand_category_grants). Per-feature overrides handle exceptions
 * and elevated actions (admin, investigate, agent grants), which categories
 * never cover.
 */
export function RoleDialog({
  open,
  role,
  onClose,
}: {
  open: boolean;
  role: RoleRow | null; // null = create mode
  onClose: () => void;
}) {
  const categories = useCategories();
  const perms = usePermissions();
  const create = useCreateRole();
  const update = useUpdateRole();

  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [grants, setGrants] = useState<Record<string, Access>>({});
  const [overrides, setOverrides] = useState<Set<string>>(new Set());
  const [showOverrides, setShowOverrides] = useState(false);

  const isCreate = role === null;
  const isSuperuser = role?.key === "superuser";

  // Seed form state whenever the dialog (re)opens for a different role.
  useEffect(() => {
    if (!open) return;
    setKey("");
    setName(role?.name ?? "");
    setDescription(role?.description ?? "");
    const seeded: Record<string, Access> = {};
    for (const g of role?.category_grants ?? []) seeded[g.category] = g.access;
    setGrants(seeded);
    setOverrides(new Set(role?.permissions.map((p) => p.id) ?? []));
    setShowOverrides(false);
  }, [open, role]);

  const overridesByCategory = useMemo(() => {
    if (!perms.data || !categories.data) return [];
    return categories.data
      .map((cat) => ({
        category: cat.name,
        perms: perms.data
          .filter((p) => cat.features.includes(p.feature))
          .sort((a, b) => a.key.localeCompare(b.key)),
      }))
      .filter((g) => g.perms.length > 0);
  }, [perms.data, categories.data]);

  // feature -> "action: what it unlocks" lines, shown as hover hints in the
  // category matrix so granting read/write isn't a guess.
  const featureHints = useMemo(() => {
    const hints = new Map<string, string>();
    const rank = (a: string) => (a === "read" ? 0 : a === "write" ? 1 : 2);
    for (const p of [...(perms.data ?? [])].sort(
      (a, b) => rank(a.action) - rank(b.action) || a.key.localeCompare(b.key),
    )) {
      const line = `${p.action}: ${p.description}`;
      const cur = hints.get(p.feature);
      hints.set(p.feature, cur ? `${cur}\n${line}` : line);
    }
    return hints;
  }, [perms.data]);

  const mutation = isCreate ? create : update;
  const valid = isCreate
    ? /^[a-z][a-z0-9_]*$/.test(key) && name.trim().length > 0
    : name.trim().length > 0;

  const save = async () => {
    const category_grants = Object.entries(grants)
      .filter((e): e is [string, CategoryAccess] => e[1] !== "none")
      .map(([category, access]) => ({ category, access }));
    const body = {
      name: name.trim(),
      description,
      category_grants,
      permission_ids: Array.from(overrides),
    };
    if (isCreate) {
      await create.mutateAsync({ key, ...body });
    } else if (isSuperuser) {
      // Superuser ignores grants in code; only name/description are editable.
      await update.mutateAsync({
        roleId: role.id,
        body: { name: body.name, description: body.description },
      });
    } else {
      await update.mutateAsync({ roleId: role.id, body });
    }
    onClose();
  };

  const toggleOverride = (id: string) => {
    setOverrides((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const OverridesChevron = showOverrides ? ChevronDown : ChevronRight;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* Single scroll container with the footer in normal flow — a sticky
          footer overlays the scrollport edge and clips whatever section ends
          up underneath it (the overrides toggle, at this content height). */}
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isCreate ? (
              "New role"
            ) : (
              <>
                Edit role <span className="font-mono text-sm">{role.key}</span>
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isSuperuser
              ? "Superuser bypasses permission checks in code; grants here have no effect."
              : "Pick an access level per category, then fine-tune with overrides if needed."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {isCreate && (
            <div>
              <Label htmlFor="role-key">Key</Label>
              <Input
                id="role-key"
                value={key}
                onChange={(e) =>
                  setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))
                }
                placeholder="e.g. revenue_team"
                className="mt-1 font-mono"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Lowercase, alphanumeric + underscores. Cannot be changed later.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="role-name">Name</Label>
              <Input
                id="role-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Revenue Team"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="role-desc">Description</Label>
              <Input
                id="role-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this role is for"
                className="mt-1"
              />
            </div>
          </div>

          {/* ── Category matrix ────────────────────────────────────────── */}
          <div className={isSuperuser ? "pointer-events-none opacity-40" : ""}>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Category access
            </h3>
            <div className="overflow-hidden rounded-md border">
              {categories.data?.map((cat, i) => {
                const value: Access = grants[cat.name] ?? "none";
                return (
                  <div
                    key={cat.name}
                    className={cn(
                      "flex items-center justify-between gap-3 px-3 py-2.5",
                      i > 0 && "border-t",
                    )}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{cat.name}</div>
                      <div className="truncate text-[11px] text-muted-foreground">
                        {cat.features.map((f, idx) => (
                          <span key={f}>
                            {idx > 0 && ", "}
                            <span
                              title={featureHints.get(f) ?? humanize(f)}
                              className="cursor-help underline decoration-dotted underline-offset-2"
                            >
                              {humanize(f)}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                    <AccessControl
                      value={value}
                      onChange={(v) =>
                        setGrants((cur) => ({ ...cur, [cat.name]: v }))
                      }
                    />
                  </div>
                );
              })}
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Write includes read. New features added to a category are covered
              automatically. Elevated actions (admin, investigate, agent grants)
              are never granted by category — use overrides below.
            </p>
          </div>

          {/* ── Per-feature overrides ──────────────────────────────────── */}
          <div className={isSuperuser ? "pointer-events-none opacity-40" : ""}>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-md border px-3 py-2.5 text-sm font-medium hover:bg-accent"
              aria-expanded={showOverrides}
              onClick={() => setShowOverrides((v) => !v)}
            >
              <OverridesChevron className="h-4 w-4 text-muted-foreground" />
              Per-feature overrides
              <span className="ml-auto rounded-md bg-neutral-light px-2 py-0.5 text-xs tabular-nums">
                {overrides.size}
              </span>
            </button>
            {showOverrides && (
              <div className="mt-2 flex flex-col gap-3">
                {overridesByCategory.map(({ category, perms: catPerms }) => (
                  <div key={category} className="rounded-md border p-3">
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {category}
                    </div>
                    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                      {catPerms.map((p) => (
                        // eslint-disable-next-line jsx-a11y/label-has-associated-control -- label content lives in the child div nodes; screen readers reach it.
                        <label
                          key={p.id}
                          className="flex cursor-pointer items-start gap-2 rounded p-1 text-sm hover:bg-accent"
                        >
                          <input
                            type="checkbox"
                            checked={overrides.has(p.id)}
                            onChange={() => toggleOverride(p.id)}
                            disabled={isSuperuser}
                            className="mt-0.5 h-4 w-4 accent-brand"
                          />
                          <div>
                            <div className="font-mono text-xs">{p.key}</div>
                            <div className="text-xs text-muted-foreground">
                              {p.description}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 border-t pt-3">
            {mutation.error && (
              <p className="min-w-0 flex-1 truncate text-sm text-destructive">
                {mutation.error instanceof Error
                  ? mutation.error.message
                  : "Failed to save."}
              </p>
            )}
            <div className="ml-auto flex shrink-0 gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={!valid || mutation.isPending} onClick={save}>
                {mutation.isPending ? "Saving…" : isCreate ? "Create" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** None / Read / Write segmented control. */
function AccessControl({
  value,
  onChange,
}: {
  value: Access;
  onChange: (v: Access) => void;
}) {
  const options: { v: Access; label: string }[] = [
    { v: "none", label: "None" },
    { v: "read", label: "Read" },
    { v: "write", label: "Write" },
  ];
  return (
    <div className="flex shrink-0 overflow-hidden rounded-md border">
      {options.map((o, i) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          aria-pressed={value === o.v}
          className={cn(
            "px-2.5 py-1 text-xs font-medium transition-colors",
            i > 0 && "border-l",
            value === o.v
              ? o.v === "none"
                ? "bg-muted text-foreground"
                : "bg-power text-white"
              : "bg-background text-muted-foreground hover:bg-accent hover:text-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
